// Production debugging utilities for S3 deployment issues
// This module provides debugging functions accessible in production

interface ProductionDebugInfo {
  timestamp: string
  environment: string
  userAgent: string
  location: string
  buildInfo: {
    mode: string
    isDev: boolean
    isProd: boolean
  }
  sessionStorageData: {
    geoDebug?: any[]
    locationDebug?: any
    finalGeoData?: any
    heatMapProps?: any
  }
  windowDebugFunctions: string[]
}

export class ProductionDebugger {
  private static instance: ProductionDebugger | null = null

  static getInstance(): ProductionDebugger {
    if (!ProductionDebugger.instance) {
      ProductionDebugger.instance = new ProductionDebugger()
    }
    return ProductionDebugger.instance
  }

  constructor() {
    this.setupGlobalDebugFunctions()
  }

  private setupGlobalDebugFunctions() {
    // Make debug functions globally available in production
    const globalThis = window as any

    globalThis.__debugRiskAssessment = () => {
      return this.getFullDebugReport()
    }

    globalThis.__debugGeographicData = () => {
      return this.getGeographicDataDebug()
    }

    globalThis.__debugSessionStorage = () => {
      return this.getSessionStorageDebug()
    }

    globalThis.__clearDebugData = () => {
      this.clearDebugData()
      console.log('🧹 Debug data cleared from sessionStorage')
    }

    globalThis.__exportDebugReport = () => {
      const report = this.getFullDebugReport()
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `risk-assessment-debug-${new Date().toISOString().slice(0, 19)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      console.log('📥 Debug report downloaded')
    }

    // Log available debug functions
    console.log('🔧 Production debug functions available:')
    console.log('  - __debugRiskAssessment() - Full debug report')
    console.log('  - __debugGeographicData() - Geographic data specific debug')
    console.log('  - __debugSessionStorage() - SessionStorage debug data')
    console.log('  - __clearDebugData() - Clear debug data')
    console.log('  - __exportDebugReport() - Download debug report as JSON')
  }

  private getFullDebugReport(): ProductionDebugInfo {
    return {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'unknown',
      userAgent: navigator.userAgent,
      location: window.location.href,
      buildInfo: {
        mode: import.meta.env.MODE,
        isDev: import.meta.env.DEV,
        isProd: import.meta.env.PROD
      },
      sessionStorageData: this.getSessionStorageDebug(),
      windowDebugFunctions: Object.keys(window as any).filter(key => key.startsWith('__debug'))
    }
  }

  private getGeographicDataDebug() {
    const sessionData = this.getSessionStorageDebug()

    return {
      timestamp: new Date().toISOString(),
      normalizationHistory: sessionData.geoDebug || [],
      locationValidation: sessionData.locationDebug,
      finalNormalizedData: sessionData.finalGeoData,
      componentPropsReceived: sessionData.heatMapProps,
      analysis: {
        dataLossDetected: this.detectDataLoss(sessionData),
        potentialCauses: this.analyzePotentialCauses(sessionData)
      }
    }
  }

  private getSessionStorageDebug() {
    const data: any = {}

    try {
      const geoDebug = sessionStorage.getItem('riskAssessment_geoDebug')
      if (geoDebug) data.geoDebug = JSON.parse(geoDebug)
    } catch (e) {
      data.geoDebugError = e instanceof Error ? e.message : 'Unknown error'
    }

    try {
      const locationDebug = sessionStorage.getItem('riskAssessment_locationDebug')
      if (locationDebug) data.locationDebug = JSON.parse(locationDebug)
    } catch (e) {
      data.locationDebugError = e instanceof Error ? e.message : 'Unknown error'
    }

    try {
      const finalGeoData = sessionStorage.getItem('riskAssessment_finalGeoData')
      if (finalGeoData) data.finalGeoData = JSON.parse(finalGeoData)
    } catch (e) {
      data.finalGeoDataError = e instanceof Error ? e.message : 'Unknown error'
    }

    try {
      const heatMapProps = sessionStorage.getItem('riskAssessment_heatMapProps')
      if (heatMapProps) data.heatMapProps = JSON.parse(heatMapProps)
    } catch (e) {
      data.heatMapPropsError = e instanceof Error ? e.message : 'Unknown error'
    }

    return data
  }

  private detectDataLoss(sessionData: any): boolean {
    // Check if data was present during normalization but absent in component
    const hasNormalizationData = sessionData.finalGeoData?.locationsCount > 0
    const hasComponentData = sessionData.heatMapProps?.propsReceived?.locations_length > 0

    return hasNormalizationData && !hasComponentData
  }

  private analyzePotentialCauses(sessionData: any): string[] {
    const causes: string[] = []

    // Check for common production issues
    if (sessionData.geoDebug && sessionData.geoDebug.length > 0) {
      const latestDebug = sessionData.geoDebug[sessionData.geoDebug.length - 1]

      if (!latestDebug.locationsIsArray && !latestDebug.distributionIsArray) {
        causes.push('Neither locations nor distribution arrays found in backend response')
      }

      if (latestDebug.locationsLength === 0 && latestDebug.distributionLength === 0) {
        causes.push('Both locations and distribution arrays are empty')
      }
    }

    if (sessionData.locationDebug?.missingFields?.length > 0) {
      causes.push(`Location objects missing required fields: ${sessionData.locationDebug.missingFields.join(', ')}`)
    }

    if (sessionData.finalGeoData?.locationsCount > 0 && sessionData.heatMapProps?.propsReceived?.locations_length === 0) {
      causes.push('Data lost between normalization and component props - likely React state or prop passing issue')
    }

    if (!sessionData.geoDebug || sessionData.geoDebug.length === 0) {
      causes.push('No normalization debug data found - normalization function may not be running')
    }

    if (!sessionData.heatMapProps) {
      causes.push('No component props debug data found - component may not be receiving props')
    }

    return causes
  }

  private clearDebugData() {
    const keys = [
      'riskAssessment_geoDebug',
      'riskAssessment_locationDebug',
      'riskAssessment_finalGeoData',
      'riskAssessment_heatMapProps'
    ]

    keys.forEach(key => {
      try {
        sessionStorage.removeItem(key)
      } catch (e) {
        // Ignore errors
      }
    })
  }

  // Method to log comparison between environments
  logEnvironmentComparison() {
    console.group('🔍 Environment Comparison Debug')
    console.log('Current environment:', import.meta.env.MODE)
    console.log('Is production build:', import.meta.env.PROD)
    console.log('User agent:', navigator.userAgent)
    console.log('Location:', window.location.href)

    const sessionDebug = this.getSessionStorageDebug()

    console.log('Debug data available:')
    console.log('  - Geographic normalization:', !!sessionDebug.geoDebug)
    console.log('  - Location validation:', !!sessionDebug.locationDebug)
    console.log('  - Final geo data:', !!sessionDebug.finalGeoData)
    console.log('  - Component props:', !!sessionDebug.heatMapProps)

    if (sessionDebug.finalGeoData) {
      console.log('Final geo data summary:')
      console.log('  - Locations count:', sessionDebug.finalGeoData.locationsCount)
      console.log('  - Concentration score:', sessionDebug.finalGeoData.concentrationScore)
      console.log('  - Sample location:', sessionDebug.finalGeoData.sampleLocation)
    }

    if (sessionDebug.heatMapProps) {
      console.log('Component props summary:')
      console.log('  - Locations received:', sessionDebug.heatMapProps.propsReceived?.locations_length)
      console.log('  - Geographic data type:', sessionDebug.heatMapProps.propsReceived?.geographic_data_type)
      console.log('  - Has geographic data:', sessionDebug.heatMapProps.propsReceived?.has_geographic_data)
    }

    console.groupEnd()
  }

  // Method to create a comprehensive test report
  createTestReport(): string {
    const debug = this.getFullDebugReport()
    const geoDebug = this.getGeographicDataDebug()

    return `
# Risk Assessment Geographic Data Debug Report

**Generated:** ${debug.timestamp}
**Environment:** ${debug.environment}
**Build Mode:** ${debug.buildInfo.mode}
**Location:** ${debug.location}

## Issue Summary
${geoDebug.analysis.dataLossDetected ? '❌ Data loss detected between normalization and component' : '✅ No data loss detected'}

## Potential Causes
${geoDebug.analysis.potentialCauses.length > 0
  ? geoDebug.analysis.potentialCauses.map(cause => `- ${cause}`).join('\n')
  : 'No specific issues identified'}

## Debug Data Summary
- **Normalization runs:** ${geoDebug.normalizationHistory?.length || 0}
- **Final locations count:** ${debug.sessionStorageData.finalGeoData?.locationsCount || 0}
- **Component locations received:** ${debug.sessionStorageData.heatMapProps?.propsReceived?.locations_length || 0}

## Detailed Data
\`\`\`json
${JSON.stringify(geoDebug, null, 2)}
\`\`\`

## Browser Information
- **User Agent:** ${debug.userAgent}
- **Available Debug Functions:** ${debug.windowDebugFunctions.join(', ')}
`
  }
}

// Initialize debugger when module loads
const productionDebugger = ProductionDebugger.getInstance()

// Export for potential manual imports
export default productionDebugger